import { useQuery } from '@tanstack/react-query';

import { listTriggerProfileRuns } from '@/lib/api/generated/api';
import type { TriggerProfileRunView } from '@/lib/api/generated/model';

import { triggerKeys } from '../queryKeys';

/**
 * One trigger profile's run history, newest first — already ordered that
 * way by the backend (`contracts/trigger-profiles-api.md`), so no client
 * re-sort here.
 *
 * Same hand-written-`queryFn` convention as `useTriggerProfiles`/
 * `useTriggerProfile`: the generated raw `listTriggerProfileRuns` fetcher,
 * not the generated `getListTriggerProfileRunsQueryOptions` helper.
 *
 * An empty `runs` array (with `isPending: false`, `loadFailed: false`) is
 * the "never run" state (FR-008) — structurally distinguishable by the
 * caller from "run but never matched" (`runs.every(r => r.signals_created
 * === 0)`), which is a non-empty, non-error result. Both are valid results
 * of this hook; the distinction is rendered by `TriggerProfileDetailSheet`,
 * not decided here.
 */
export function useTriggerProfileRuns(
  workspaceId: string,
  triggerProfileId: string,
) {
  const query = useQuery({
    queryKey: triggerKeys.runs(workspaceId, triggerProfileId),
    queryFn: async (): Promise<TriggerProfileRunView[]> => {
      const response = await listTriggerProfileRuns(
        workspaceId,
        triggerProfileId,
      );
      if (response.status !== 200) {
        throw new Error('The run history could not be loaded.');
      }
      return response.data;
    },
    retry: false,
  });

  return {
    runs: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
  };
}
